import { useCallback, useState } from "react";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { processError } from "../../../../common/utils/base_error";
import { useHabilidadesFilters } from "../hooks/Habilidades_lista/filters";
import { useHabilidadesPagination } from "../hooks/Habilidades_lista/pagination";
import { Habilidade } from "../../domain/models/habilidade";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { HabilidadeForm } from "../schemas/Habilidades_lista/habilidade";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";

/**
 * LoadHabilidadesState
 */

type LoadHabilidadesState = {
  tipoPesquisa?: number;
  valueField?: string;
  page?: number;
  rowsPerPage?: number;
};

/**
 * useHabilidadesListaViewModel
 */

export const useHabilidadesListaViewModel = () => {
  const {
    commons: {
      getAllLogAlteracaoUseCase,
      getAllLogAlteracaoByParentUseCase,
    },
    rh: {
      getAllHabilidadeUseCase,
      createHabilidadeUseCase,
      updateHabilidadeUseCase,
      deleteHabilidadeUseCase,
    },
  } = useDependencyInjection();

  const debouncedRef = useCreateDebounceHandler(750);

  const { pushErrorNotification, pushInfoNotification } = usePageLoader();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string>();

  const [selectedHabilidade, setSelectedHabilidade] = useState<Habilidade>();

  const [habilidades, setHabilidades] = useState<Habilidade[]>();

  const [openCadastroModal, setOpenCadastroModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const filters = useHabilidadesFilters();
  const pagination = useHabilidadesPagination();

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoUseCase,
    getAllLogAlteracaoByParentUseCase,
    onSetError: (error) => pushErrorNotification("Erro", [error]),
  });

  /**
   * Funções de carregamento
   */

  const loadHabilidades = useCallback(async (funcState?: LoadHabilidadesState) => {
    try {
      const filteredTipoPesquisa = funcState?.tipoPesquisa ?? filters.tipoPesquisa;
      const filteredValueField = funcState?.valueField ?? filters.valueField;

      const filteredPage = funcState?.page ?? pagination.page;
      const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllHabilidadeUseCase.getAllHabilidade(
        filteredTipoPesquisa,
        filteredValueField,
        filteredPage,
        filteredRowsPerPage,
      );

      setHabilidades(getAllHabilidadeUseCase.items);

      pagination.setRowsPerPage(getAllHabilidadeUseCase.pageSize);
      pagination.setTotalItems(getAllHabilidadeUseCase.totalItems);
    } catch (e: unknown) {
      setHabilidades([]);
      throw e;
    }
  }, [
    filters,
    pagination,
    getAllHabilidadeUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      setErrorMessage(undefined);
      await loadHabilidades();
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar habilidades!")[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadHabilidades();
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar habilidades!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadHabilidades,
    pushErrorNotification,
  ]);

  const handleSubmit = useCallback(async (form: HabilidadeForm) => {
    try {
      setLoading(true);
      setSaving(true);

      if (selectedHabilidade) {
        await updateHabilidadeUseCase.updateHabilidade(
          selectedHabilidade.idHabilidade,
          form.descricao,
          form.habilidade,
        );
      } else {
        await createHabilidadeUseCase.createHabilidade(
          form.descricao,
          form.habilidade,
        );
      }

      await loadHabilidades();
      pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar habilidades!'));
    } finally {
      setLoading(false);
      setSaving(false);
      setOpenCadastroModal(false);
    }
  }, [
    selectedHabilidade,
    createHabilidadeUseCase,
    updateHabilidadeUseCase,
    loadHabilidades,
    pushErrorNotification,
    pushInfoNotification,
  ]);

  const handleTipoPesquisaChange = useDebouncedCallback(debouncedRef, (tipoPesquisa: number) => {
    useHabilidadesFilters.getState().setTipoPesquisa(tipoPesquisa);
    return async () => {
      try {
        setLoading(true);
        useHabilidadesPagination.getState().setPage(1);
        await loadHabilidades({ tipoPesquisa, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar habilidades!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadHabilidades,
    pushErrorNotification,
  ]);

  const handleValueFieldChange = useDebouncedCallback(debouncedRef, (valueField: string) => {
    useHabilidadesFilters.getState().setValueField(valueField);
    return async () => {
      try {
        setLoading(true);
        useHabilidadesPagination.getState().setPage(1);
        await loadHabilidades({ valueField, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar habilidades!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadHabilidades,
    pushErrorNotification,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useHabilidadesPagination.getState().setPage(page + 1);
      await loadHabilidades({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar habilidades!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadHabilidades,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useHabilidadesPagination.getState().setPage(1);
      useHabilidadesPagination.getState().setRowsPerPage(rowsPerPage);
      await loadHabilidades({ rowsPerPage, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar habilidades!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadHabilidades,
    pushErrorNotification,
  ]);

  const handleHabilidadeCadastroOpenState = useCallback(async (idHabilidade: number | null, openState: boolean) => {
    if (openState) {
      if (idHabilidade && habilidades) {
        setSelectedHabilidade(habilidades.find(x => x.idHabilidade === idHabilidade));
      } else {
        setSelectedHabilidade(undefined);
      }
    }
    setOpenCadastroModal(openState);
  }, [
    habilidades
  ]);

  const handleHabilidadeDeleteOpenState = useCallback(async (idHabilidade: number | null, openState: boolean) => {
    if (openState) {
      if (idHabilidade && habilidades) {
        setSelectedHabilidade(habilidades.find(x => x.idHabilidade === idHabilidade));
      } else {
        setSelectedHabilidade(undefined);
      }
    }
    setOpenDeleteModal(openState);
  }, [
    habilidades
  ]);

  const handleConfirmarDeletarHabilidade = useCallback(async () => {
    if (selectedHabilidade) {
      try {
        setSaving(true);
        await deleteHabilidadeUseCase.deleteHabilidade(selectedHabilidade.idHabilidade);
        await loadHabilidades();
        pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar habilidades!'));
      } finally {
        setSaving(false);
        setOpenDeleteModal(false);
      }
    }
  }, [
    selectedHabilidade,
    deleteHabilidadeUseCase,
    loadHabilidades,
    pushErrorNotification,
    pushInfoNotification,
  ]);

  const handleOpenChangeHistory = useCallback(() => {
    if (selectedHabilidade) {
      changeHistory.handleOpenChangeHistory(selectedHabilidade.idHabilidade, LogTipoTabela.HABILIDADE, false);
    }
  }, [
    selectedHabilidade,
    changeHistory,
  ]);

  return {
    loading,
    saving,
    errorMessage,
    selectedHabilidade,
    habilidades: habilidades ?? [],
    openCadastroModal,
    openDeleteModal,
    filters,
    pagination,
    changeHistory,
    handleReload,
    handleSubmit,
    handleTipoPesquisaChange,
    handleValueFieldChange,
    handlePageChange,
    handleRowsPerPageChange,
    handleHabilidadeCadastroOpenState,
    handleHabilidadeDeleteOpenState,
    handleConfirmarDeletarHabilidade,
    handleOpenChangeHistory,
  };
}